import { useCallback, useRef, useState } from 'react'
import { TocIcon } from '~/components/icons'
import { useClickOutside } from '~/hooks/useClickOutside'
import { useTableOfContent } from './useTableOfContent'
import { Button } from '~/components/general/Button'
import type { BLOCKS } from '@contentful/rich-text-types'
import classNames from 'classnames'
import { fadeInFadeOutLargeTw } from '~/styles/mixins'

export type ContentfulRichTextBlock = keyof typeof BLOCKS

export type TOCStructure = Array<TOCItem>
export type TOCHeading = Extract<ContentfulRichTextBlock, 'HEADING_2' | 'HEADING_3'>
export type TOCItem = { text: string; id: string; type: TOCHeading; subItems?: Array<TOCItem> }

export type TableOfContentProps = {
  items: TOCStructure
  richTextArticle: HTMLElement | null
}

export const TableOfContent = ({ items, richTextArticle }: TableOfContentProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isOpened, setIsOpened] = useState(false)
  useClickOutside(ref, () => setIsOpened(false))

  const topicItems = items.map((item) => [item, ...(item.subItems || [])]).flat()
  const activeItemId = useTableOfContent({ items: topicItems, richTextArticle })

  return (
    <div className="relative" ref={ref}>
      <Button
        type={'button'}
        color={'white'}
        className={
          '!p-0 !size-13 justify-center border-transparent !bg-neutral-5 hover:!bg-neutral-10 active:!bg-neutral-10 lg:mb-[14px]'
        }
        iconRight={<TocIcon />}
        onClick={() => setIsOpened((isOpened) => !isOpened)}
      />

      <ul
        className={classNames(
          `shadow-light_high transition-opacity duration-300 max-2xl:rounded-3xl max-2xl:max-h-[70vh] max-2xl:top-[calc(100%+6px)] max-2xl:bg-white p-0 m-0 w-[257px] max-2xl:absolute max-2xl:left-0 max-2xl:overflow-x-hidden max-2xl:overflow-y-auto ${fadeInFadeOutLargeTw(
            isOpened,
          )}`,
          isOpened ? 'opacity-100' : 'opacity-0',
        )}
      >
        {items.map((item, index) => (
          <TableOfContentItem
            key={index}
            {...item}
            richTextArticle={richTextArticle}
            activeId={activeItemId}
          />
        ))}
      </ul>
    </div>
  )
}

type TableOfContentItemProps = TOCItem & {
  richTextArticle: HTMLElement | null
  activeId: string
}

const TableOfContentItem = ({
  id,
  text,
  richTextArticle,
  activeId,
  subItems,
}: TableOfContentItemProps) => {
  const handleClick = useCallback(
    (typeHeading: 'h2' | 'h3', id: string) => {
      if (!richTextArticle) return
      const heading = richTextArticle.querySelector(`${typeHeading}[data-id="${id}"]`)
      if (!heading) return

      window.scrollTo({ top: window.scrollY + heading.getBoundingClientRect().top - 84 })
    },
    [richTextArticle],
  )

  return (
    <>
      <li
        key={id}
        onClick={() => handleClick('h2', id)}
        className={classNames(
          'duration-200 transition-colors hover:bg-neutral-5 text-s list-none py-4 px-6 max-xl:rounded-none rounded-[30px]',
          {
            'bg-neutral-5 cursor-default': activeId === id,
            'cursor-pointer': activeId !== id,
          },
        )}
      >
        {text}
      </li>
      {subItems?.map((item) => {
        return (
          <li
            onClick={() => handleClick('h3', item.id)}
            key={item.id}
            className={classNames(
              'duration-200 transition-colors hover:bg-neutral-5 text-s list-none py-4 px-6 max-xl:rounded-none rounded-[30px]',
              {
                'bg-neutral-5 cursor-default': activeId === item.id,
                'cursor-pointer': activeId !== item.id,
              },
            )}
          >
            {item.text}
          </li>
        )
      })}
    </>
  )
}
